import { useCallback } from 'react';
import { DEFAULT_CEREBRO_API_BASE, useAssistantStore, generateMessageId } from '@/stores/useAssistantStore';
import type { ChatMessage, TTSRequest, TTSResponse, DAWRequest, DAWResponse, CommandResponse } from '@/types/assistant';

function getHeaders(token: string) {
  const h: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (token) h['X-Ark-Auth'] = token;
  return h;
}

function getLocalAudioHeaders() {
  return { 'Content-Type': 'application/json' };
}

function base64FromBlob(blob: Blob): Promise<string> {
  return blob.arrayBuffer().then((arrayBuffer) => {
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    // Keep the conversion bounded so longer microphone recordings do not
    // overflow the browser call stack before they reach the local runtime.
    for (let offset = 0; offset < bytes.length; offset += 8192) {
      binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
    }
    return btoa(binary);
  });
}

function formatFromMimeType(mimeType: string): string {
  const subtype = mimeType.split('/')[1]?.split(';')[0]?.toLowerCase() || '';
  if (['wav', 'webm', 'ogg', 'flac', 'mp3', 'm4a'].includes(subtype)) return subtype;
  return 'webm';
}

function audioUrlFromResponse(data: TTSResponse): string | null {
  if (data.audio_url) return data.audio_url;
  if (!data.audio) return null;
  const binary = atob(data.audio);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: `audio/${data.format || 'wav'}` }));
}

export function useCerebroAPI() {
  const { apiBase, localAudioBase, authToken, addMessage, updateMessage, setLoading } = useAssistantStore();
  const controlBase = apiBase || DEFAULT_CEREBRO_API_BASE;
  const audioBase = localAudioBase.replace(/\/$/, '');

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch(`${controlBase}/health`, { headers: getHeaders(authToken) });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, [controlBase, authToken]);

  const sendCommand = useCallback(async (transcript: string): Promise<CommandResponse | null> => {
    const msgId = generateMessageId();
    const userMsg: ChatMessage = {
      id: msgId,
      role: 'user',
      content: transcript,
      timestamp: Date.now(),
      metadata: { type: 'command', status: 'pending' }
    };
    addMessage(userMsg);
    setLoading(true);

    try {
      const res = await fetch(`${controlBase}/v1/command`, {
        method: 'POST',
        headers: getHeaders(authToken),
        body: JSON.stringify({
          transcript,
          context: { previous_commands: [], active_apps: ['SPⱯƇZƎ™'] },
          user_id: 'architect_001'
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: CommandResponse = await res.json();

      updateMessage(msgId, {
        metadata: { ...userMsg.metadata, commandId: data.command_id, status: 'success' }
      });

      const assistantMsg: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: `Command parsed: ${data.intent} (confidence: ${Math.round(data.confidence * 100)}%). Authority: ${data.authority_required}.`,
        timestamp: Date.now(),
        metadata: { type: 'command', commandId: data.command_id, status: 'success' }
      };
      addMessage(assistantMsg);
      setLoading(false);
      return data;
    } catch (err) {
      updateMessage(msgId, { metadata: { ...userMsg.metadata, status: 'error' } });
      const errorMsg: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: `Command failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        timestamp: Date.now(),
        metadata: { type: 'system', status: 'error' }
      };
      addMessage(errorMsg);
      setLoading(false);
      return null;
    }
  }, [controlBase, authToken, addMessage, updateMessage, setLoading]);

  const sendTTS = useCallback(async (text: string, voiceProfile = 'fiona', options: Partial<TTSRequest> = {}): Promise<string | null> => {
    const msgId = generateMessageId();
    addMessage({
      id: msgId,
      role: 'user',
      content: `Synthesize: "${text}" (${voiceProfile})`,
      timestamp: Date.now(),
      metadata: { type: 'tts', status: 'pending' }
    });
    setLoading(true);

    try {
      const req: TTSRequest = {
        text,
        voice_profile: voiceProfile,
        speed: 1.0,
        pitch: 0.0,
        format: 'wav',
        post_process: { eq_preset: 'warm-vocal', compression: true, reverb: false },
        ...options,
      };
      const res = await fetch(`${audioBase}/v1/tts`, {
        method: 'POST',
        headers: getLocalAudioHeaders(),
        body: JSON.stringify(req)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: TTSResponse = await res.json();
      const audioUrl = audioUrlFromResponse(data);
      if (!audioUrl) throw new Error('Local TTS returned no playable audio.');

      updateMessage(msgId, { metadata: { type: 'tts', status: 'success' } });
      const assistantMsg: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: `TTS synthesized locally by ${data.source || 'the docked audio runtime'}.`,
        timestamp: Date.now(),
        metadata: { type: 'tts', status: 'success' }
      };
      addMessage(assistantMsg);
      setLoading(false);
      return audioUrl;
    } catch (err) {
      updateMessage(msgId, { metadata: { type: 'tts', status: 'error' } });
      addMessage({
        id: generateMessageId(),
        role: 'assistant',
        content: `TTS failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        timestamp: Date.now(),
        metadata: { type: 'system', status: 'error' }
      });
      setLoading(false);
      return null;
    }
  }, [audioBase, addMessage, updateMessage, setLoading]);

  const sendDAW = useCallback(async (action: string, target?: string, parameters?: Record<string, unknown>): Promise<DAWResponse | null> => {
    const msgId = generateMessageId();
    addMessage({
      id: msgId,
      role: 'user',
      content: `DAW: ${action}${target ? ` → ${target}` : ''}`,
      timestamp: Date.now(),
      metadata: { type: 'daw', status: 'pending' }
    });
    setLoading(true);

    try {
      const req: DAWRequest = { action, target: target || 'global', parameters };
      const res = await fetch(`${audioBase}/v1/daw`, {
        method: 'POST',
        headers: getLocalAudioHeaders(),
        body: JSON.stringify(req)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: DAWResponse = await res.json();

      updateMessage(msgId, { metadata: { type: 'daw', status: 'success' } });
      const assistantMsg: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: `DAW command applied: ${action}. Status: ${data.status}.${data.client_attachment === 'UNVERIFIED' ? ' No attached audio client has confirmed playback or capture.' : ''}`,
        timestamp: Date.now(),
        metadata: { type: 'daw', status: 'success' }
      };
      addMessage(assistantMsg);
      setLoading(false);
      return data;
    } catch (err) {
      updateMessage(msgId, { metadata: { type: 'daw', status: 'error' } });
      addMessage({
        id: generateMessageId(),
        role: 'assistant',
        content: `DAW command failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        timestamp: Date.now(),
        metadata: { type: 'system', status: 'error' }
      });
      setLoading(false);
      return null;
    }
  }, [audioBase, addMessage, updateMessage, setLoading]);

  const transcribeAudio = useCallback(async (audio: Blob, language = 'en') => {
    if (audio.size === 0) throw new Error('No microphone audio was captured.');
    // The local API enforces a 50 MB JSON limit. The base64 payload expands
    // binary audio, so reject before sending a request it cannot accept.
    if (audio.size > 35 * 1024 * 1024) throw new Error('Voice capture exceeds the local transcription size limit.');
    const response = await fetch(`${audioBase}/v1/stt`, {
      method: 'POST',
      headers: getLocalAudioHeaders(),
      body: JSON.stringify({
        audio: await base64FromBlob(audio),
        audio_format: formatFromMimeType(audio.type),
        language,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || typeof payload.text !== 'string' || payload.text.trim().length === 0) {
      throw new Error(payload.message || payload.error || `Local STT returned HTTP ${response.status}.`);
    }
    return {
      text: payload.text.trim(),
      language: typeof payload.language === 'string' ? payload.language : language,
      engine: typeof payload.engine === 'string' ? payload.engine : 'local-stt',
      isFinal: payload.isFinal === true,
    };
  }, [audioBase]);

  const sendChat = useCallback(async (content: string) => {
    const msgId = generateMessageId();
    addMessage({
      id: msgId,
      role: 'user',
      content,
      timestamp: Date.now(),
      metadata: { type: 'chat', status: 'pending' }
    });
    setLoading(true);

    // Route through command parser for now
    try {
      const res = await fetch(`${controlBase}/v1/command`, {
        method: 'POST',
        headers: getHeaders(authToken),
        body: JSON.stringify({
          transcript: content,
          context: { previous_commands: [], active_apps: ['SPⱯƇZƎ™'] },
          user_id: 'architect_001'
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      updateMessage(msgId, { metadata: { type: 'chat', status: 'success' } });
      const assistantMsg: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: `Understood. Intent: ${data.intent}. Domain: ${data.domain}.`,
        timestamp: Date.now(),
        metadata: { type: 'chat', status: 'success' }
      };
      addMessage(assistantMsg);
    } catch {
      updateMessage(msgId, { metadata: { type: 'chat', status: 'error' } });
      addMessage({
        id: generateMessageId(),
        role: 'assistant',
        content: 'Connection to CEREBRØ_X API unavailable. Running in offline mode.',
        timestamp: Date.now(),
        metadata: { type: 'system', status: 'error' }
      });
    } finally {
      setLoading(false);
    }
  }, [controlBase, authToken, addMessage, updateMessage, setLoading]);

  return { fetchHealth, sendCommand, sendTTS, sendDAW, sendChat, transcribeAudio };
}
